import { useState, useEffect, useRef } from "react";
import QrLoginScanner from "../components/shared/QrLoginScanner";
import { safeSet, safeGet, safeRemove } from "../utils/safeStorage";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Fingerprint, QrCode } from "lucide-react";
import axios from "axios";
import { useAuth, API } from "../contexts/AuthContext";
import { isBiometricAvailable, isBiometricEnabled, authenticateBiometric } from "../utils/biometric";
import { useTranslation, changeLanguage, getLanguage, SUPPORTED_LANGUAGES } from "@/i18n";
import PasswordInput from "../components/shared/PasswordInput";
import { passwordChecks } from "../utils/passwordRules";

export const LoginPage = () => {
  const { t } = useTranslation();
  const [isRegister, setIsRegister] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState("");
  // Turnstile (bot check). V18.97: the screen ASKS THE SERVER for the site
  // key when it opens. It used to come only from REACT_APP_TURNSTILE_SITE_KEY,
  // baked in at build time - on 10 Oct the host had the secret (check on) and
  // the key under a name the build never read, so there was no widget, no
  // token, and every sign-up got "Verification failed". A key baked into the
  // build still counts; the server's answer wins when it has one.
  const [turnstileSiteKey, setTurnstileSiteKey] = useState(process.env.REACT_APP_TURNSTILE_SITE_KEY || "");
  // The check is ON at the server but there is no key to show it with: no
  // sign-up can succeed, so say so instead of letting each one fail.
  const [signupUnavailable, setSignupUnavailable] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const turnstileRef = useRef(null);
  const [usePinLogin, setUsePinLogin] = useState(false);
  const [useCodeLogin, setUseCodeLogin] = useState(false);
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [accessCode, setAccessCode] = useState("");
  const [email, setEmail] = useState(safeGet("userEmail") || "");
  const [password, setPassword] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [phone, setPhone] = useState("");
  // Home currency chosen at signup - defaults to the region-detected currency.
  const [pin, setPin] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showVerifyMessage, setShowVerifyMessage] = useState(false);
  const [resending, setResending] = useState(false);
  const [biometricAvailable, setBiometricAvailable] = useState(false);
  const [biometricEnabledState, setBiometricEnabledState] = useState(false);
  // Ref for the hidden PIN input. We focus it imperatively after mount
  // (and whenever PIN mode is activated) because iOS Safari/Capacitor
  // is unreliable with React's autoFocus on hidden inputs - the
  // keyboard often doesn't open until the user taps explicitly.
  const pinInputRef = useRef(null);
  // Ref for the password input - focused imperatively when switching
  // from PIN mode to password mode so the keyboard opens immediately.
  const passwordInputRef = useRef(null);
  const { loginWithGoogle, loginWithFacebook, login, loginWithPin, quickLogin, register, resendVerification } = useAuth();

  // Where to go after signing in.
  //
  // ProtectedRoute now passes the page the user was trying to reach, so a
  // list link tapped from a notify message lands on THAT LIST rather than the
  // lists index. Falls back to home when there is nowhere to return to.
  // (12 Sep 2026)
  const afterLogin = () => location.state?.from?.pathname || "/";
  // Compact language dropdown: flag + code on the button, full names in the
  // list (mirrors the Settings language list). Device locale is the default.
  const [langOpen, setLangOpen] = useState(false);
  const LANG_FLAGS = { en:'🇦🇺', es:'🇪🇸', fr:'🇫🇷', de:'🇩🇪', it:'🇮🇹', pt:'🇵🇹', nl:'🇳🇱', pl:'🇵🇱', ru:'🇷🇺', uk:'🇺🇦', tr:'🇹🇷', ar:'🇸🇦', hi:'🇮🇳', zh:'🇨🇳', ja:'🇯🇵', ko:'🇰🇷', vi:'🇻🇳', th:'🇹🇭', id:'🇮🇩', ms:'🇲🇾', sv:'🇸🇪', no:'🇳🇴', da:'🇩🇰', fi:'🇫🇮', cs:'🇨🇿', sk:'🇸🇰', hu:'🇭🇺', ro:'🇷🇴', bg:'🇧🇬', el:'🇬🇷', he:'🇮🇱', 'sr-Latn':'🇷🇸', sr:'🇷🇸', hr:'🇭🇷', bs:'🇧🇦', sl:'🇸🇮', mk:'🇲🇰', sq:'🇦🇱' };
  const langFlag = (c) => LANG_FLAGS[c] || '🌐';
  const langShort = (c) => (c === 'sr-Latn' ? 'SR' : c.toUpperCase());

  // Live signup helpers: instant email-exists check + password checklist.
  const [emailExists, setEmailExists] = useState(false);
  // Google Sign-In: only active when the OAuth client id is configured.
  const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || '';
  useEffect(() => {
    // Turnstile loader (v15.23, fixed v16.48). Runs only when a site key is
    // configured, so the preview stays untouched while dormant.
    //
    // THE BUG THIS FIXES: Cloudflare's api.js auto-renders elements with class
    // cf-turnstile ONCE, at script load. Our widget div only exists while
    // isRegister is true, and the script is injected when the LOGIN view
    // mounts - so Cloudflare scanned an empty DOM, found nothing, and never
    // looked again. Switching to Create Account then showed a div nobody was
    // watching: no widget, no token, and the backend correctly rejected the
    // empty token as "Verification failed".
    //
    // Fix: load the script with render=explicit and call turnstile.render()
    // ourselves each time the register view appears. Explicit rendering is
    // also the only way to reset the widget after a failed attempt - tokens
    // are single-use and expire in ~5 minutes, so a retry with a stale token
    // would fail forever.
    if (!turnstileSiteKey) return;
    window.onTurnstileToken = (token) => setTurnstileToken(token || "");
    window.onTurnstileExpired = () => setTurnstileToken("");
    if (!document.querySelector('script[data-turnstile]')) {
      const sc = document.createElement("script");
      sc.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      sc.async = true;
      sc.defer = true;
      sc.setAttribute("data-turnstile", "1");
      document.head.appendChild(sc);
    }
  }, [turnstileSiteKey]);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/auth/signup-check`).then((r) => {
      if (!alive || !r || !r.data) return;
      if (r.data.site_key) setTurnstileSiteKey(r.data.site_key);
      setSignupUnavailable(!!r.data.enabled && !r.data.site_key
        && !process.env.REACT_APP_TURNSTILE_SITE_KEY);
    }).catch(() => { /* older server, or offline - keep the build's key */ });
    return () => { alive = false; };
  }, []);

  // Render (and re-render) the widget whenever the register view is showing.
  useEffect(() => {
    if (!turnstileSiteKey || !isRegister) return;
    let cancelled = false;
    let tries = 0;

    const mount = () => {
      if (cancelled) return;
      const el = turnstileRef.current;
      // api.js is async - poll briefly until it lands rather than racing it.
      if (!window.turnstile || !el) {
        if (tries++ < 60) setTimeout(mount, 100);
        return;
      }
      if (el.dataset.rendered === "1") return;
      try {
        window.turnstile.render(el, {
          sitekey: turnstileSiteKey,
          callback: (token) => setTurnstileToken(token || ""),
          "expired-callback": () => setTurnstileToken(""),
          "error-callback": () => setTurnstileToken(""),
        });
        el.dataset.rendered = "1";
      } catch (e) {
        // Already rendered, or a duplicate mount under StrictMode - harmless.
      }
    };
    mount();

    return () => {
      cancelled = true;
      const el = turnstileRef.current;
      if (el) {
        try { window.turnstile && window.turnstile.remove(el); } catch (e) { /* not rendered */ }
        delete el.dataset.rendered;
      }
      setTurnstileToken("");
    };
  }, [turnstileSiteKey, isRegister]);

  useEffect(() => {
    if (!FACEBOOK_APP_ID || window.FB) return;
    window.fbAsyncInit = function () {
      window.FB.init({ appId: FACEBOOK_APP_ID, cookie: true, version: 'v19.0' });
    };
    const sc = document.createElement('script');
    sc.src = 'https://connect.facebook.net/en_US/sdk.js';
    sc.async = true;
    document.head.appendChild(sc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [FACEBOOK_APP_ID]);
  const handleFacebookLogin = () => {
    if (!window.FB) return;
    window.FB.login((resp) => {
      const at = resp?.authResponse?.accessToken;
      if (!at) return;
      loginWithFacebook(at).catch(() =>
        toast.error(t('login.facebookFailed') || 'Facebook sign-in failed. Try again.'));
    }, { scope: 'email' });
  };
  const googleBtnRef = useRef(null);
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !googleBtnRef.current) return;
    const init = () => {
      if (!window.google?.accounts?.id || !googleBtnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (resp) => {
          try {
            await loginWithGoogle(resp.credential);
          } catch (e) {
            toast.error(t('login.googleFailed') || 'Google sign-in failed. Try again.');
          }
        },
      });
      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: 'outline', size: 'large', width: 320, text: 'continue_with',
      });
    };
    if (window.google?.accounts?.id) { init(); return; }
    const sc = document.createElement('script');
    sc.src = 'https://accounts.google.com/gsi/client';
    sc.async = true; sc.onload = init;
    document.head.appendChild(sc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [GOOGLE_CLIENT_ID]);
  const checkEmailExists = async () => {
    if (!isRegister || !email.includes('@')) return;
    try {
      const r = await axios.post(`${API}/auth/check-email`, { email: email.toLowerCase() });
      setEmailExists(!!r.data.exists);
    } catch (e) { setEmailExists(false); }
  };
  // Rules live in utils/passwordRules so this screen and the reset screen
  // cannot drift apart - see the note in that file.
  const PW_LABELS = {
    len: t('login.pwMin') || 'At least 8 characters',
    up: t('login.pwUpper') || 'One uppercase letter',
    low: t('login.pwLower') || 'One lowercase letter',
    num: t('login.pwNumber') || 'One number',
    special: t('login.pwSpecial') || 'One special character (! @ # $ ...)',
  };
  const pwChecks = passwordChecks(password).map((c) => ({ ...c, label: PW_LABELS[c.key] }));
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const savedEmail = safeGet("userEmail");
    const hasPinEnabled = safeGet("hasPinEnabled");
    if (savedEmail && hasPinEnabled === "true") {
      setUsePinLogin(true);
      setEmail(savedEmail);
    }

    // Check biometric availability
    (async () => {
      try {
        const avail = await isBiometricAvailable();
        setBiometricAvailable(avail.available);
        if (avail.available) {
          const enabled = await isBiometricEnabled();
          setBiometricEnabledState(enabled);
        }
      } catch (e) {
        // Silent - biometric just won't show
      }
    })();
  }, []);

  // Focus the PIN input whenever PIN mode is entered. Imperative focus
  // is more reliable than autoFocus on iOS / Capacitor, especially after
  // route lazy-load where the input may not exist on first effect run.
  // The 100ms delay gives the input time to mount and lets iOS process
  // the focus request after the route transition settles.
  useEffect(() => {
    if (usePinLogin && pinInputRef.current) {
      const t = setTimeout(() => {
        pinInputRef.current?.focus();
        // Also dispatch a click; some iOS versions need this synthetic
        // user-initiated event to bring the keyboard up reliably
        pinInputRef.current?.click();
      }, 100);
      return () => clearTimeout(t);
    }
  }, [usePinLogin]);

  const handleBiometricLogin = async () => {
    setSubmitting(true);
    try {
      const creds = await authenticateBiometric();
      const res = await login(creds.email, creds.password);
      if (res.user.has_pin) safeSet("hasPinEnabled", "true");
      toast.success(t('login.welcomeBack'));
      navigate(afterLogin());
    } catch (err) {
      // Don't show error if user just cancelled
      if (err?.message && !err.message.toLowerCase().includes("cancel")) {
        toast.error(t('login.biometricFailed'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  // A scan is an unambiguous action by the person holding the phone, so it
  // redeems immediately rather than filling the box and waiting for a second
  // tap. The server's single-use and expiry rules still apply.
  const handleScannedCode = async (code) => {
    setShowQrScanner(false);
    setAccessCode(code);
    try {
      await quickLogin(code);
      toast.success(t('login.welcomeToApp'));
      navigate(afterLogin());
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('join.failed'));
    }
  };

  // One PIN submit path for both the Unlock button and auto-unlock on the
  // last typed digit. Takes the PIN value explicitly - the auto path fires
  // from onChange, before React state has re-rendered, so reading the `pin`
  // state there would submit a stale 3-digit value.
  const submitPin = async (pinValue) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      await loginWithPin(email, pinValue);
      toast.success(t('login.welcomeBack'));
      navigate(afterLogin());
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Something went wrong";
      const detailLower = detail.toLowerCase();
      if (
        status === 429 ||
        detailLower.includes("too many") ||
        detailLower.includes("locked")
      ) {
        toast.error(`${detail} Or tap "Forgot password" to reset.`, { duration: 8000 });
      } else if (detailLower.includes("verify your email")) {
        // The PIN path had no branch for this, so a blocked sign-in showed a
        // toast and nothing else - no resend button, no way forward. The
        // password path has offered one all along. (12 Sep 2026)
        setShowVerifyMessage(true);
        toast.error(detail);
      } else if (status === 404 || detailLower.includes("no account") ||
                 detailLower.includes("not found")) {
        // The account this device remembers no longer exists - deleted, or the
        // email was changed. hasPinEnabled is a DEVICE flag that logout
        // deliberately keeps, so nothing server-side ever clears it, and the
        // PIN screen kept reappearing for an account that was gone (v16.55).
        // Drop the stale flags and fall back to normal sign-in.
        safeRemove("hasPinEnabled");
        safeRemove("userEmail");
        setUsePinLogin(false);
        setPin("");
        toast.error(t('login.accountGone') || "That account no longer exists. Please sign in or create a new account.");
        return;
      } else {
        toast.error(detail);
      }
      // Wrong PIN: clear the boxes and refocus so the retry starts clean
      // instead of sitting on four filled dots.
      setPin("");
      setTimeout(() => pinInputRef.current?.focus(), 50);
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (usePinLogin && !isRegister && !useCodeLogin) {
      await submitPin(pin);
      return;
    }
    setSubmitting(true);
    try {
      if (isRegister) {
        if (password !== confirmPassword) {
          toast.error(t('settings.passwordsNoMatch'));
          setSubmitting(false);
          return;
        }
        if (signupUnavailable) {
          toast.error(t('login.signupUnavailable'));
          setSubmitting(false);
          return;
        }
        if (turnstileSiteKey && !turnstileToken) {
          toast.error(t('login.verifyHuman') || 'Please complete the verification.');
          setSubmitting(false);
          return;
        }
        const res = await register(email, password, turnstileToken);
        // Save the chosen home currency for this account/device.
        if (res?.requires_verification) {
          // Email verification required - show verify message
          toast.success(t('login.accountCreated'));
          setShowVerifyMessage(true);
          setIsRegister(false);
          setPassword("");
        } else {
          // Auto-verified - log them in immediately
          const loginRes = await login(email, password);
          if (loginRes.user.has_pin) safeSet("hasPinEnabled", "true");
          toast.success(t('login.welcomeToApp'));
          navigate(afterLogin());
        }
      } else if (useCodeLogin) {
        await quickLogin(accessCode);
        toast.success(t('login.welcomeToApp'));
        navigate(afterLogin());
      } else {
        const res = await login(email, password);
        if (res.user.has_pin) {
          safeSet("hasPinEnabled", "true");
        }
        toast.success(t('login.welcomeBack'));
        navigate(afterLogin());
      }
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Something went wrong";
      const detailLower = detail.toLowerCase();

      if (detailLower.includes("verify your email")) {
        setShowVerifyMessage(true);
        toast.error(detail);
      } else if (
        status === 429 ||
        detailLower.includes("too many") ||
        detailLower.includes("locked")
      ) {
        // Rate-limited / locked out - show a longer toast with the
        // backend's wait time and a hint about password reset.
        toast.error(
          `${detail} Or tap "Forgot password" to reset.`,
          { duration: 8000 }
        );
      } else {
        toast.error(detail);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      toast.error(t('login.enterEmailFirst'));
      return;
    }
    setResending(true);
    try {
      await resendVerification(email);
      toast.success(t('login.verifEmailSent'));
    } catch (err) {
      toast.error(t('login.failResend'));
    } finally {
      setResending(false);
    }
  };

  /** Forget whoever this device remembers, without needing to sign in. */
  const forgetSavedAccount = () => {
    safeRemove("userEmail");
    safeRemove("hasPinEnabled");
    safeRemove("familyName");
    setEmail("");
    setPin("");
    setUsePinLogin(false);
    setShowVerifyMessage(false);
  };

  const switchToPasswordLogin = () => {
    setUsePinLogin(false);
    setPin("");
    // Focus password input after React renders it. iOS needs an
    // imperative focus call to bring up the keyboard.
    setTimeout(() => {
      passwordInputRef.current?.focus();
    }, 100);
  };

  // Reverse of switchToPasswordLogin. The PIN/password choice is otherwise
  // made once at mount, so a user who typed an email on the password view
  // had no way into PIN entry without reloading the app (requested 13 Aug).
  // Only offered when this DEVICE has completed a PIN login before
  // (hasPinEnabled) - the same signal the mount-time decision uses. The
  // /login-pin endpoint still verifies the PIN server-side per account, so
  // a mismatched email simply fails with the normal wrong-PIN error.
  const switchToPinLogin = () => {
    setUsePinLogin(true);
    setPassword("");
    // Focus is handled by the existing [usePinLogin] effect above.
  };

  return (
    <div className="app-container px-8 flex flex-col justify-center min-h-screen">
      {showQrScanner && (
        <QrLoginScanner
          onCode={handleScannedCode}
          onClose={() => setShowQrScanner(false)}
        />
      )}
      {/* Language dropdown - device locale by default, full names in the list */}
      <div className="absolute top-4 right-4 z-30" data-testid="login-language-switch">
        <button
          type="button"
          onClick={() => setLangOpen((v) => !v)}
          className="px-3 py-1.5 rounded-full text-sm font-semibold border border-gray-300 dark:border-gray-600 bg-white/80 dark:bg-gray-800/80 flex items-center gap-1.5"
          data-testid="login-language-btn"
        >
          {langFlag(getLanguage())} {langShort(getLanguage())} <span className="text-gray-400">▾</span>
        </button>
        {langOpen && (
          <div className="absolute right-0 mt-2 w-56 max-h-80 overflow-y-auto rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl py-1" data-scroll-allow="true">
            {SUPPORTED_LANGUAGES.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => { changeLanguage(l.code); setLangOpen(false); }}
                className={`w-full text-left px-4 py-2.5 text-sm flex items-center gap-2.5 ${getLanguage() === l.code ? 'font-bold bg-gray-100 dark:bg-gray-700' : ''}`}
              >
                <span>{langFlag(l.code)}</span> {l.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="mb-12 text-center relative z-10">
        <h1 className="text-4xl font-bold tracking-tight mb-2">Xelanti</h1>
        <p className="text-gray-500">{t('login.tagline')}</p>
      </div>

      {(GOOGLE_CLIENT_ID || FACEBOOK_APP_ID) && (
        <div className="relative z-10 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
            <span className="text-xs text-gray-400">{t('login.orContinueWith') || 'or continue with'}</span>
            <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
          </div>
          <div className="flex flex-col items-center gap-2">
            {GOOGLE_CLIENT_ID && <div ref={googleBtnRef} data-testid="google-signin"></div>}
            {FACEBOOK_APP_ID && (
              <button
                type="button"
                onClick={handleFacebookLogin}
                className="btn-cap px-6 rounded-full bg-[#1877F2] text-white font-semibold flex items-center gap-2"
                style={{ width: 320, justifyContent: 'center' }}
                data-testid="facebook-signin"
              >
                <span className="font-black">f</span> {t('login.continueFacebook') || 'Continue with Facebook'}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="relative z-10">
        <h2 className="text-xl font-semibold mb-8 text-center">
          {isRegister ? t('login.createAccount') : useCodeLogin ? t('login.enterYourCode') : usePinLogin ? t('login.enterPin') : t('login.signIn')}
        </h2>

        {showVerifyMessage && (
          <div className="mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl">
            <p className="text-sm text-blue-800 dark:text-blue-200 font-medium mb-2">
              📧 {t('login.verifyEmailNotice')}
            </p>
            <p className="text-xs text-blue-700 dark:text-blue-300 mb-3">
              {t('login.verifSentTo')} <strong>{email}</strong>. {t('login.clickActivate')}
            </p>
            <button
              type="button"
              onClick={handleResendVerification}
              disabled={resending}
              className="text-xs font-semibold text-blue-700 dark:text-blue-300 underline disabled:opacity-50"
            >
              {resending ? t('login.sending') : t('login.resendVerif')}
            </button>
          </div>
        )}
        
        <form onSubmit={handleSubmit} className="space-y-6">
          {useCodeLogin && (
            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">{t('login.sixDigitCode')}</label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                className="clean-input text-center text-3xl tracking-[0.4em] font-bold"
                placeholder="······"
                autoComplete="one-time-code"
                autoFocus
                data-testid="access-code-input"
              />
              <p className="text-xs text-gray-400 mt-2 text-center">{t('login.askOwnerForCode')}</p>
              {/* Faster than typing when the owner's phone is in front of you */}
              <button
                type="button"
                onClick={() => setShowQrScanner(true)}
                className="w-full mt-4 py-2.5 rounded-full border-2 border-black dark:border-white font-semibold text-sm flex items-center justify-center gap-2"
                data-testid="scan-qr-button"
              >
                <QrCode size={18} /> {t('join.scanInstead')}
              </button>
            </div>
          )}

          {!usePinLogin && !useCodeLogin && (
            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">
                {t('settings.email')}
              </label>
              <input
                type="email"
                value={email}
                // Lowercased here, not with CSS: accounts are stored lowercase, so a
                // field that only LOOKS lowercase would misreport what it holds.
                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                className="clean-input"
                placeholder="you@example.com"
                required
                autoCapitalize="off"
                autoCorrect="off"
                onBlur={checkEmailExists}
                data-testid="email-input"
              />
              {isRegister && emailExists && (
                <p className="text-xs text-red-500 mt-1" data-testid="email-exists-warning">
                  {t('login.emailExists') || 'This email is already registered.'}{' '}
                  <button type="button" className="underline font-medium" onClick={() => { setIsRegister(false); setEmailExists(false); }}>
                    {t('login.logInInstead') || 'Log in instead'}
                  </button>
                </p>
              )}
            </div>
          )}
          
          {!useCodeLogin && (usePinLogin ? (
            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">
                {t('login.pinFor', { email })}
              </label>
              {/* PIN input: a single, invisible text input sits ON TOP of
                  4 decorative boxes. Tapping anywhere on the row focuses
                  the input directly (iOS requires the focused element to
                  be a real interactable surface - a hidden input with
                  pointer-events:none can't be focused reliably from a
                  user tap). The boxes underneath are pure display. */}
              <div className="relative my-6">
                <div className="flex justify-center gap-3 pointer-events-none">
                  {[0, 1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className={`w-14 h-14 flex items-center justify-center text-2xl font-bold border-2 rounded-xl bg-white dark:bg-gray-800 ${
                        pin.length === i
                          ? 'border-black dark:border-white'
                          : 'border-gray-200 dark:border-gray-700'
                      }`}
                    >
                      <span className="text-3xl">{pin[i] ? "•" : ""}</span>
                    </div>
                  ))}
                </div>
                <input
                  ref={pinInputRef}
                  type="tel"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={4}
                  value={pin}
                  onChange={(e) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
                    setPin(digits);
                    // Auto-unlock the moment the 4th digit lands - no Unlock
                    // tap needed. Settings toggle "pinAutoUnlock" (default
                    // ON; only an explicit 'false' disables it). Wrong PIN
                    // clears and refocuses inside submitPin for a clean retry.
                    if (digits.length === 4 && safeGet("pinAutoUnlock") !== "false" && !submitting) {
                      submitPin(digits);
                    }
                  }}
                  className="absolute inset-0 w-full h-full bg-transparent border-0 outline-none"
                  style={{
                    color: 'transparent',
                    caretColor: 'transparent',
                    fontSize: '16px', // Prevents iOS zoom on focus
                    textAlign: 'center',
                  }}
                  data-testid="pin-input"
                  autoComplete="one-time-code"
                  aria-label={t('login.pinAria')}
                  autoFocus
                />
              </div>
              <button
                type="button"
                onClick={switchToPasswordLogin}
                className="text-sm text-gray-500 hover:text-black w-full text-center"
              >
                {t('login.usePassword')}
              </button>
              {/* A WAY OUT. (13 Sep 2026)
                  Switching to a password keeps the same address prefilled, so
                  until now there was no obvious route to a different account -
                  and if the remembered one is blocked on verification, that is
                  a keypad with no exit. This forgets the device's saved
                  identity without needing to sign in first. */}
              <button
                type="button"
                data-testid="not-you-btn"
                onClick={forgetSavedAccount}
                className="text-sm text-gray-400 hover:text-black w-full text-center mt-2"
              >
                {t('login.notYou')}
              </button>
            </div>
          ) : (
            <div>
              <label className="text-sm font-medium text-gray-500 mb-1 block">
                {t('login.password')}
              </label>
              <PasswordInput
                inputRef={passwordInputRef}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                inputClassName="clean-input"
                placeholder="••••••••"
                required
                minLength={6}
                data-testid="password-input"
              />
              {isRegister && password.length > 0 && (
                <div className="mt-2 space-y-0.5" data-testid="password-checklist">
                  {pwChecks.map((c) => (
                    <p key={c.key} className={`text-xs flex items-center gap-1.5 ${c.ok ? 'text-green-600 dark:text-green-400' : 'text-red-400'}`}>
                      <span>{c.ok ? '✓' : '✗'}</span> {c.label}
                    </p>
                  ))}
                </div>
              )}
              {isRegister && (
                <p className="text-xs text-gray-400 mt-1">{t('login.atLeast6')}</p>
              )}
              {isRegister && (
                <div className="mt-4">
                  <label className="text-sm font-medium text-gray-500 mb-1 block">
                    {t('reset.confirmPassword')}
                  </label>
                  <PasswordInput
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    inputClassName="clean-input"
                    placeholder="••••••••"
                    required
                    minLength={6}
                    data-testid="confirm-password-input"
                  />
                  {confirmPassword && confirmPassword !== password && (
                    <p className="text-xs text-red-500 mt-1">{t('settings.passwordsNoMatch')}</p>
                  )}
                </div>
              )}
              {isRegister && signupUnavailable && (
                <p className="text-sm text-red-500 mt-3" data-testid="signup-unavailable">
                  {t('login.signupUnavailable')}
                </p>
              )}
              {isRegister && turnstileSiteKey && (
                <div
                  ref={turnstileRef}
                  className="mt-3"
                  data-testid="turnstile-widget"
                />
              )}
            </div>
          ))}

          <button 
            type="submit" 
            className="clean-btn-primary w-full mt-8"
            disabled={submitting || (usePinLogin && pin.length !== 4) || (useCodeLogin && accessCode.length !== 6)}
            data-testid="login-submit-btn"
          >
            {submitting ? "..." : (isRegister ? t('login.createAccount') : useCodeLogin ? t('login.enterApp') : usePinLogin ? t('login.unlock') : t('login.signIn'))}
          </button>
        </form>

        {!isRegister && biometricAvailable && biometricEnabledState && (
          <button
            type="button"
            onClick={handleBiometricLogin}
            disabled={submitting}
            className="w-full mt-4 flex items-center justify-center gap-2 py-3 border-2 border-gray-200 dark:border-gray-700 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors disabled:opacity-50"
            data-testid="biometric-login-btn"
          >
            <Fingerprint size={20} />
            {t('login.useBiometric')}
          </button>
        )}

        {!usePinLogin && !useCodeLogin && (
          <button 
            onClick={() => { setIsRegister(!isRegister); setShowVerifyMessage(false); }}
            className="w-full mt-6 text-center text-gray-500 hover:text-black transition-colors"
            data-testid="toggle-auth-mode"
          >
            {isRegister ? t('login.toggleToSignIn') : t('login.toggleToCreate')}
          </button>
        )}
        
        {!isRegister && !usePinLogin && !useCodeLogin && (
          <button 
            onClick={() => navigate('/forgot-password')}
            className="w-full mt-4 text-center text-sm text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="forgot-password-link"
          >
            {t('login.forgotPassword')}
          </button>
        )}

        {/* Switch to PIN entry without reloading. Shown only when this
            device has used PIN login before AND an email is present, so the
            link never appears for someone who has no PIN to enter. */}
        {!isRegister && !usePinLogin && !useCodeLogin &&
          email.trim() !== "" && safeGet("hasPinEnabled") === "true" && (
          <button
            type="button"
            onClick={switchToPinLogin}
            className="w-full mt-4 text-center text-sm font-medium text-gray-500 hover:text-black transition-colors"
            data-testid="use-pin-link"
          >
            {t('login.usePin')}
          </button>
        )}

        {/* Easy-login: redeem a code from the account owner */}
        {!isRegister && !usePinLogin && !useCodeLogin && (
          <button
            onClick={() => { setUseCodeLogin(true); setShowVerifyMessage(false); setAccessCode(""); }}
            className="w-full mt-4 text-center text-sm font-medium text-gray-500 hover:text-black transition-colors"
            data-testid="i-have-a-code-link"
          >
            {t('login.iHaveCode')}
          </button>
        )}

        {useCodeLogin && (
          <button
            onClick={() => { setUseCodeLogin(false); setAccessCode(""); }}
            className="w-full mt-6 text-center text-gray-500 hover:text-black transition-colors"
            data-testid="back-to-login-link"
          >
            {t('login.backToNormal')}
          </button>
        )}

        <div className="mt-8 flex items-center justify-center gap-2 text-xs text-gray-400">
          <button onClick={() => navigate('/privacy')} className="underline hover:text-gray-600 transition-colors" data-testid="login-privacy-link">{t('legal.privacy')}</button>
          <span aria-hidden="true">·</span>
          <button onClick={() => navigate('/terms')} className="underline hover:text-gray-600 transition-colors" data-testid="login-terms-link">{t('legal.termsShort')}</button>
        </div>
      </div>
    </div>
  );
};
